import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import TextField from '@atlaskit/textfield';
import {
  AppKey,
  Environment,
  MarieSchemaAnalytics,
  OfferingCatalog,
  SanitizedFormattedHtmlMessage,
  Url,
  useBXPExpressClient,
  useStargateClient,
  Storage
} from '@atlassiansox/bxpkit-core';
import { Base64 } from 'js-base64';
import { useAppSelector } from '../../../../redux';
import {
  trackActivateProductsSubmit,
  trackDeferSitenameSignups,
  trackFormSubmit,
  trackN2ESubmit,
  trackN2ESubmit2,
  trackRecaptchaError,
  trackSignupButtonClicked,
  trackSignupContextTooLarge,
  trackSignUpFormSubmitFailed,
  trackSignUpFormSubmitSucceeded,
  trackSubmitWithCaptcha,
  trackErrorParsingAnchorJourneyCookie
} from '../../../../service/sign-up-form/analytics';
import { checkProductEdition } from '../../../../service/sign-up-form/product-edition';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  getTranslatableError,
  messages
} from '../../../../messages/sign-up-form';
import {
  HandleSubmitType,
  Product,
  ProductKeys,
  ProductPayloadObject,
  Sites,
  SubmitErrorIDType,
  SubmitErrorType,
  WacSignupFormWithIntlProps
} from '../../../../model/wac-signup-form';
import { createSolutionStatsEvent } from '../../../../service/sign-up-form/analytics';
import {
  FormPayloadActivateProducts,
  formSubmitN2N,
  isTestTenantByName,
  pollForSuccess
} from '../../../../service/sign-up-form/form-submit';
import {
  checkBreachStatus,
  productsCanBeInBreachOfFree
} from '../../../../service/sign-up-form/free-breach';
import { CheckEmailPage } from '../check-email-page';
import {
  BitbucketOrganizationPicker,
  shouldRenderBitbucketOrgPicker
} from './components/bitbucket-organization-picker';
import {
  BitbucketAccountPicker,
  BitbucketJiraInfoBox,
  BreachInfoBox,
  DisplayNameField,
  EmailField,
  GdprLocation,
  InfoBox,
  MigrationSource,
  SiteNameField,
  SitePickerField,
  SubmitButton
} from './components';
import { CarouselEnd } from './components/carousel-end';
import { FormWelcomeMessage } from './components/form-welcome-message';
import {
  ErrorMessageWithDefaultClassName,
  Eula,
  EulaChooseSignup,
  EulaDefaultExpandedEmail,
  MarketingConsent,
  NoCard,
  NoCardDefaultExpandedEmail,
  ReCaptchaEula,
  ReCaptchaEulaDefaultExpandedEmail,
  ResendEmailContainer,
  WacSignupFormContainer,
  WelcomeDiv,
  InputSiteNameMessage,
  BitbucketFieldsContainer,
  FieldLabel,
  WorkspaceIdPrefix,
  TextFieldWrapper,
  HelperMessageContainer,
  OrgPickerElementMargin,
  OrgProvisioningProgressContainer,
  CCPSiteMigrationContainer
} from './components/styled';

import {
  useSignUpFormConfig,
  useSignUpFormGlobalConfiguration,
  useSignUpFormUIState,
  useSiteNameGeneration
} from '../../../../hook';

import { isNotUndefined } from '@atlassiansox/bxp-shared-utils';
import { useDeferSitenameExposed } from '../../../../hook/use-defer-sitename-exposed';
import { getDeferSitenameExperimentCohort } from '../utility/get-defer-sitename-experiment-cohort';
import { nextUrl } from '../utility/new-url';
import { EulaComponent } from './components/eula-component';
import {
  getRedirectUrlFromShadowItError,
  isResponseError
} from '../../../../utility';
import Cookies from 'js-cookie';
import {
  useExperiment,
  useFeatureGate
} from '@atlassiansox/bxp-web-statsig-client';
import { sanitizeCustomSignupContext } from '../../../../utility/sanitize-custom-signup-context';
import {
  checkBitbucketWorkspaceSlugAvailability,
  generateBitbucketWorkspaceId,
  handleOrgCreate
} from '../../../../service/sign-up-form/bitbucket-form-submit';
import { trackBitbucketWorkspaceProvFailure } from '../../../../service/sign-up-form/bitbucket-analytics';
import { default as AKButton } from '@atlaskit/button';

const offeringCatalog = new OfferingCatalog(Environment.getEnvironment());

function getOpsgenieAdvancedSettings(dataRegion?: string | null) {
  return {
    advancedSettings: {
      additionalProvisioningData: {
        dataRegion: dataRegion === 'EU' ? 'europe' : 'main'
      },
      initialProductURL:
        dataRegion === 'EU'
          ? 'atlassian-app.eu.opsgenie.com'
          : 'atlassian-app.opsgenie.com'
    }
  };
}
// shipit-55 SHPLV-364
type MakeAppInstallTypes = {
  readonly siteName: string;
  readonly appKey: string;
  readonly xsrfToken: string;
};
type UserSignupResponse = {
  id: string;
  otpRedirect?: string;
};

function getBillingAdvancedSettings() {
  const queryParameters = Url.getUrlParams() || {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const additionalProvisioningData: { [key: string]: any } = {};

  if (queryParameters.hasOwnProperty('billingMode')) {
    additionalProvisioningData.billingMode = queryParameters.billingMode;
  }
  if (queryParameters.hasOwnProperty('billingCode')) {
    additionalProvisioningData.billingCode = queryParameters.billingCode;
  }

  return {
    advancedSettings: {
      additionalProvisioningData
    }
  };
}

export function getBitbucketAdvancedSettings(
  isStaging: boolean,
  existingUser?: boolean | null
) {
  const suppressEmail = existingUser ? undefined : true;
  const advancedSettingsObject = {
    advancedSettings: {
      initialProductURL: isStaging ? 'integration.bb-inf.net' : 'bitbucket.org',
      additionalProvisioningData: {
        signupLocation: 'WAC'
      }
    }
  };
  if (suppressEmail !== undefined) {
    Object.assign(advancedSettingsObject.advancedSettings, { suppressEmail });
  }
  return advancedSettingsObject;
}

export function getBitbucketSignupAction(
  createBB: boolean,
  createJSW: boolean
) {
  const createdJSW = createJSW ? 'created-jira-software.ondemand' : '';
  const createdBB = createBB ? 'created-bitbucket.ondemand' : '';
  const signupUrl = window?.location?.href || '';

  return {
    advancedSettings: {
      additionalProvisioningData: {
        signupUrl,
        signupAction: `${createdBB}${
          createBB && createJSW ? '-' : ''
        }${createdJSW}`
      }
    }
  };
}

export function getStatuspageAdvancedSettings(
  isStaging: boolean,
  spProduct?: string
) {
  return {
    advancedSettings: {
      initialProductURL: isStaging
        ? 'manage.statuspagestaging.com'
        : 'manage.statuspage.io',
      additionalProvisioningData: {
        signupLocation: 'WAC',
        spProduct: spProduct || 'public'
      }
    }
  };
}

export function getDevopsAdvancedSettings() {
  return {
    advancedSettings: {
      additionalProvisioningData: {
        dataRegion: 'main',
        signupDevops: true
      }
    }
  };
}

export function getRequestId(progressUri: string) {
  const pattern = new RegExp(/cloud\/[\w-]+\/status\/([\w-]+)$/);
  const matches = pattern.exec(progressUri);
  return matches ? matches[1] : '';
}

export function shouldSkipRecaptcha(email: string) {
  const botTestPattern =
    /(bottest@usersinbuckets.com|bottest@atl-paid-dev.com|bottest@bxpwpi.com)$/;
  return botTestPattern.test(email);
}

const DEVELOPER_EXPERIMENT_KEY = 'PUR-7747';

const WORKSPACE_VALIDATION_PATTERN: RegExp = /^[a-z0-9_-]+$/;

export function WacSignupForm(formProps: WacSignupFormWithIntlProps) {
  const {
    devMode,
    migrationData,
    showEmail,
    hideFullName,
    userInfo,
    bitbucketWorkspaces,
    marketingConsentRequired,
    signInAccountUrl,
    switchAccountUrl,
    originAnalyticsAttributes,
    productKeys,
    isStaging,
    wacEnvUrl,
    analyticsTrack,
    formExistingSiteShown = () => undefined,
    deferSiteNameCreation,
    templateId,
    enableFreeBreachTest = false,
    simulateFreeBreachState = false,
    spProduct,
    hideGreeting = false,
    outlineOnFocus = false,
    popOverError = false,
    fadeOnDisable = false,
    showWarningIcon = false,
    hideCaptchaEula = false,
    selectedSiteId = '',
    siteRenamePageUrl,
    enableSkipRecaptcha = true,
    intl,
    allowSignupWithAllSocialOptions,
    teamType
  } = formProps;

  const {
    edition: editionOverride,
    useCcpPlatform,
    platform,
    experimentScreensFlags,
    customSignupContext
  } = useSignUpFormConfig();
  const { featureFlags, deferredSiteName } = useAppSelector((store) => store);
  const {
    enableTintinMvp,
    provisionSecondPartyApps,
    enableDeferredFullName,
    enableDefaultExpandedEmail
  } = featureFlags;

  const { createAnalyticsEvent } = useAnalyticsEvents();
  const stargateClient = useStargateClient();
  const signUpFormUIState = useSignUpFormUIState();
  const signupSource = new URLSearchParams(window.location.search).get(
    'signupSource'
  );
  const bitbucketPreferred = signupSource === 'bbbundle' ? true : false;
  const isPaid = !Url.getUrl()?.includes('/try/cloud/signup');
  const isNewUser = !!signUpFormUIState.signupState.isNewUser;
  const experiment = useExperiment('frontend_site_name_additional_copy');
  const { site_name_additional_copy, site_name_generation } = experiment.value;
  const isFreeJiraOrConfluence =
    (signUpFormUIState.isJsw || signUpFormUIState.isConfluence) &&
    signUpFormUIState.isFree &&
    productKeys.length === 1;
  const isNewUserInJiraOrConfluenceNotPaid =
    isNewUser && isFreeJiraOrConfluence && !isPaid;
  const isNewUserInSiteNameExperiment =
    (site_name_additional_copy || site_name_generation) &&
    isNewUserInJiraOrConfluenceNotPaid;

  const { newRelic, segment } = analyticsTrack;
  const context = useContext(MarieSchemaAnalytics);

  const { confirmationPageUrl, checkEmailPageUrl, newUserSignupRedirectUrl } =
    useSignUpFormGlobalConfiguration();

  const generatedCloudName = useSiteNameGeneration();

  const [exposedToDeferSitename, setExposedToDeferSitename] =
    useState<boolean>(false);

  const [isFocusedChooseSignup, setIsFocusedChooseSignup] = useState(true);
  const [showError, setShowError] = useState(!!signUpFormUIState.isConfluence);
  const [formReset, setFormReset] = useState(false);
  const [recommendedSiteName, setRecommendedSiteName] = useState<
    string | undefined
  >(undefined);

  const signupButtonText = isNewUserInSiteNameExperiment
    ? messages.devopsCarouselButton
    : signUpFormUIState.isConfluence
    ? messages.startButtonTextChooseSignup
    : allowSignupWithAllSocialOptions
    ? messages.devopsButtonText
    : signUpFormUIState.buttonText;

  const productName = signUpFormUIState.isJsw ? 'Jira' : 'Confluence';

  useEffect(() => {
    const cartedProducts = productKeys.map((k) => k.product);

    segment('bxp.formCloudSignup.viewedCheckoutStep', {
      productAttributes: {
        cartedProducts,
        intendedProduct: cartedProducts[0]
      }
    });
    segment('bxp.formCloudSignup.addedProduct', {
      cartedProducts
    });
  }, []);

  useEffect(() => {
    async function getRecommendedSiteName() {
      if (
        userInfo?.email &&
        site_name_generation &&
        isNewUserInSiteNameExperiment
      ) {
        const { recommendedName } = await stargateClient.getRecommendedSiteName(
          { email: userInfo.email, webPlatform: 'magnolia' }
        );
        setRecommendedSiteName(recommendedName);
      }
    }
    getRecommendedSiteName().catch((err) => err);
  }, [recommendedSiteName]);

  useEffect(() => {
    // Trigger isSubmitting state change to avoid race condition on form submit
    signUpFormUIState.setSignupState({
      ...signUpFormUIState.signupState,
      isSubmitting: false
    });
  }, [formReset]);

  useDeferSitenameExposed({
    createAnalyticsEvent,
    products: signUpFormUIState.signupState.products.map(
      (product) => product?.product as string
    ),
    exposedToDeferSitename
  });

  if (migrationData && migrationData.invalidMigration) {
    return (
      <MigrationSource isClaimedMigration={migrationData.claimedMigration} />
    );
  }

  // SHIPIT55
  const bxpExpressClient = useBXPExpressClient();

  function wrappedClientReq({
    siteName,
    appKey,
    xsrfToken
  }: MakeAppInstallTypes) {
    return new Promise(async (resolve) => {
      const response = await bxpExpressClient
        .marketplaceAppInstall({
          siteName,
          appKey,
          xsrfToken
        })
        .catch((err) => err);
      resolve(response);
    });
  }

  useEffect(() => {
    // free-breach-test
    if (
      enableFreeBreachTest &&
      signUpFormUIState.signupState.selectedSite &&
      signUpFormUIState.signupState.products &&
      productsCanBeInBreachOfFree(signUpFormUIState.signupState.products)
    ) {
      signUpFormUIState.setDisableSignInWhileTestingFreeBreach(true);
      checkBreachStatus({
        stargateClient,
        cloudId: signUpFormUIState.signupState.selectedSite.cloudId,
        productKeys: signUpFormUIState.signupState.products.map(
          (productKey) => productKey.product
        ) as string[]
      })
        .then((siteInBreach: boolean) => {
          segment('bxpSignupFreeUserBreachSitePickerInBreach', {
            selectedSiteId: signUpFormUIState.signupState.selectedSite?.cloudId
          });
          signUpFormUIState.setSelectedSiteIsInBreachOfFree(siteInBreach);
          signUpFormUIState.setDisableSignInWhileTestingFreeBreach(false);
        })
        .catch((exception) => {
          segment('bxpSignupFreeUserBreachSitePickerError', {
            exception: JSON.stringify(exception)
          });
        });

      if (simulateFreeBreachState) {
        segment('bxpSignupFreeUserBreachSitePickerInBreach', {
          selectedSiteId: signUpFormUIState.signupState.selectedSite?.cloudId
        });
        signUpFormUIState.setSelectedSiteIsInBreachOfFree(true);
      }
    }
  }, [
    signUpFormUIState.isBbCopyEnabled,
    signUpFormUIState.signupState.cloudName,
    signUpFormUIState.signupState.selectedSite,
    signUpFormUIState.signupState.isExistingSitePickerN2E,
    enableFreeBreachTest
  ]);

  const findIfJswIncluded = (cloudId: string | undefined): boolean => {
    const siteProducts = userInfo?.sites?.filter(
      (site) => site.cloudId === cloudId
    )[0].products;

    if (!siteProducts || !cloudId) {
      return false;
    }

    return siteProducts.includes(ProductKeys.JIRA_SOFTWARE);
  };

  const handleSubmit = async ({
    payload,
    activateProducts = false,
    cloudId = '',
    isJswIncluded,
    gRecaptchaResponse,
    productEdition
  }: HandleSubmitType) => {
    if (!payload || !payload.products || payload.products.length < 1) {
      return false;
    }

    Storage.setLocalStorage('consentConfig', JSON.stringify(payload.consent));

    let referrerHash;
    if (sessionStorage) {
      referrerHash = sessionStorage.getItem('sContext');
    }

    const rHashDecoded = decodeURIComponent(
      escape(window.atob(referrerHash || ''))
    );
    const combinedHash = window.btoa(
      unescape(
        encodeURIComponent(
          [
            rHashDecoded,
            document.referrer.replace('|', ''),
            gRecaptchaResponse
          ].join('|')
        )
      )
    );

    let payloadProducts = payload.products.map(
      (p: Product): ProductPayloadObject => {
        const returnObj = {
          accountName: payload.cloudName || undefined
        };

        //  We do not set edition for Opsgenie
        if (p.product !== 'opsgenie') {
          Object.assign(returnObj, { edition: editionOverride || p.edition });
        }
        if (
          p.product === ProductKeys.JIRA_SOFTWARE &&
          signUpFormUIState.isBundleBBJSW
        ) {
          Object.assign(returnObj, {
            edition: productEdition || 'free'
          });
        }

        if (signUpFormUIState.signupState.migrationId) {
          Object.assign(returnObj, {
            migrationSourceUuid: signUpFormUIState.signupState.migrationId,
            edition:
              p.edition !== 'standard' && p.edition !== 'premium'
                ? 'standard'
                : p.edition
          });
        }

        return {
          ...returnObj,
          [`product${activateProducts ? 'Key' : ''}`]: p.product
        };
      }
    );

    // shipit55 apps bundle SHPLV-364
    const productsIncludesApps = payloadProducts
      .map(({ product }) => {
        return Object.values(AppKey).includes(product as AppKey);
      })
      .includes(true);

    if (productsIncludesApps) {
      payloadProducts = _.remove(payloadProducts, ({ product }) => {
        return !Object.values(AppKey).includes(product as AppKey);
      });
    }

    // Check if 2nd Party App Provisioning Eligibility applies:
    // Redirect if conditions are met (single-product JSW Free Edition
    // New Site signups, no template/dev/migration signups).
    const isAppProvisioningEligible =
      provisionSecondPartyApps &&
      !devMode &&
      !templateId &&
      !migrationData &&
      payloadProducts.length === 1 &&
      payloadProducts.map(({ edition }) => edition).includes('free') &&
      payloadProducts
        .map(({ product }) => product)
        .includes(ProductKeys.JIRA_SOFTWARE);

    const isTestTenant = isTestTenantByName(isStaging, payload.cloudName);

    const cofsPayload = {
      adminUsername: 'admin',
      cloudName:
        payload.cloudName ||
        (deferredSiteName.isEnabled ? generatedCloudName : null),
      adminPassword: payload.password,
      email: payload.email,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      firstName: payload.firstName,
      lastName: payload.lastName,
      products: payloadProducts,
      consent: payload.consent,
      gRecaptchaResponse: combinedHash,
      advancedSettings: {}
    };

    const signupContext = {};
    if (templateId) {
      Object.assign(signupContext, { templateId });
    }
    // expcen-2603 cut 50 remove after experiment -> experiment has been removed but still needed for another teams experiment
    if (
      payload.products.length === 1 &&
      payload.products[0].product === ProductKeys.JIRA_SOFTWARE &&
      payload.products[0].edition === 'free'
    ) {
      Object.assign(signupContext, {
        isCutOnboardingStepsInHalfEnabled: true
      });
    }
    if (deferSiteNameCreation && !signUpFormUIState.signupState.isLoggedIn) {
      if (signUpFormUIState.isStatuspage) {
        // For statuspage, do not include the page path so the user
        // will be directed straight to Statuspage
        Object.assign(signupContext, {
          autogenSiteName: 1
        });
      } else {
        Object.assign(signupContext, {
          pagePath: siteRenamePageUrl,
          autogenSiteName: 1
        });
      }
      cofsPayload.cloudName = null;
    }

    const bxpGatewayAnchorCookie = Cookies.get('bxp_gateway_anchor') || '';
    if (bxpGatewayAnchorCookie) {
      try {
        const bxpGatewayAnchor = JSON.parse(bxpGatewayAnchorCookie);
        if (bxpGatewayAnchor) {
          bxpGatewayAnchor.timestamp = new Date();
          Object.assign(signupContext, { bxpGatewayAnchor });
        }
      } catch (error) {
        trackErrorParsingAnchorJourneyCookie({ createAnalyticsEvent });
      }
    }

    if (isTestTenant) {
      Object.assign(cofsPayload, { testingPurposes: true });
    }
    // 2nd party app provision skip integration screen in product
    if (isAppProvisioningEligible) {
      Object.assign(signupContext, { skipIntegrationsSurvey: true });
    }

    // slack promo confluence hack yay!!!!
    if (experimentScreensFlags?.enableSlackPromoConfluence) {
      Object.assign(signupContext, { slackPromoConfluence: true });
    }

    // custom signup context passed in from CMS configured Signup Form
    if (customSignupContext) {
      const sanitizedCustomSignupContext =
        sanitizeCustomSignupContext(customSignupContext);
      Object.assign(signupContext, {
        customizedContextData: sanitizedCustomSignupContext
      });
    }

    const MAX_SIGNUP_CONTEXT_LENGTH = 8192;
    const signupContextString: string = JSON.stringify(signupContext);
    if (signupContextString.length <= MAX_SIGNUP_CONTEXT_LENGTH) {
      Object.assign(cofsPayload, {
        signupContext: signupContextString
      });
    } else {
      trackSignupContextTooLarge({
        contextLength: signupContextString.length,
        newRelic
      });
    }

    const activateProductsPayload = {
      products: payloadProducts,
      cloudName: payload.cloudName
    };
    // suppress emails if you are not a confluence only sign up
    if (
      !(
        payload.products.length === 1 &&
        payload.products[0].product === ProductKeys.CONFLUENCE
      )
    ) {
      Object.assign(cofsPayload, { advancedSettings: { suppressEmail: true } });
      Object.assign(activateProductsPayload, {
        advancedSettings: { suppressEmail: true }
      });
    }
    const bbWorkSpace = signUpFormUIState.bbWorkspaces.filter(
      (obj) => obj.workspace.name === payload.bbName
    )[0];
    const workspaceUuid = bbWorkSpace?.workspace?.uuid;

    trackActivateProductsSubmit({
      activateProducts,
      segment
    });

    if (activateProducts) {
      Object.assign(activateProductsPayload, { activateProducts }, { cloudId });
      if (!signUpFormUIState.isBbCopyEnabled && bbWorkSpace !== undefined) {
        Object.assign(activateProductsPayload, {
          advancedSettings: {
            additionalProvisioningData: {
              workspaceUuid
            }
          }
        });
      }
      if (signUpFormUIState.isBundleBBJSW) {
        _.merge(
          activateProductsPayload,
          getBitbucketSignupAction(
            bbWorkSpace === undefined,
            !payload?.isExistingSitePickerN2E ||
              (payload?.selectedSite?.products &&
                !payload?.selectedSite?.products.includes(
                  ProductKeys.JIRA_SOFTWARE
                )) ||
              false
          )
        );
      }
      if (signUpFormUIState.isOpsgenie) {
        _.merge(
          activateProductsPayload,
          getOpsgenieAdvancedSettings(signUpFormUIState.signupState.dataRegion)
        );
      }
      if (bitbucketPreferred) {
        _.merge(
          activateProductsPayload,
          getBitbucketAdvancedSettings(isStaging, payload?.isLoggedIn)
        );
      }

      if (signUpFormUIState.isStatuspage) {
        _.merge(
          activateProductsPayload,
          getStatuspageAdvancedSettings(isStaging, spProduct)
        );
      }

      _.merge(activateProductsPayload, getBillingAdvancedSettings());

      const solutionStatsEvent = signUpFormUIState.isBundleBBJSW
        ? createSolutionStatsEvent({
            createAnalyticsEvent,
            signUpFormUIState
          })
        : undefined;

      return formSubmitN2N({
        payload: activateProductsPayload,
        wacEnvUrl,
        confirmationPageUrl,
        checkEmailPageUrl,
        isStaging,
        isLoggedIn: userInfo?.isLoggedIn,
        isJswIncluded,
        solutionStatsEvent,
        isOpsgenieIncluded: signUpFormUIState.isOpsgenie,
        dataRegion: payload.dataRegion,
        setPollingLoaderShown: signUpFormUIState.setPollingLoaderShown,
        analyticsTrack,
        setSubmitError: signUpFormUIState.setSubmitError,
        resendEmail: signUpFormUIState.resendEmail,
        hideUserSegmentation: signUpFormUIState.hideUserSegmentation
      });
    }

    if (!signUpFormUIState.isBbCopyEnabled && bbWorkSpace !== undefined) {
      Object.assign(cofsPayload, {
        advancedSettings: {
          additionalProvisioningData: {
            workspaceUuid
          }
        }
      });
      _.merge(
        cofsPayload,
        getBitbucketSignupAction(
          bbWorkSpace === undefined,
          !payload?.isExistingSitePickerN2E ||
            (payload?.selectedSite?.products &&
              !payload?.selectedSite?.products.includes(
                ProductKeys.JIRA_SOFTWARE
              )) ||
            false
        )
      );
    }

    // for Devops payloads, avoid setting initialProductURL
    // (gets set otherwise if a payload includes OG)
    if (signUpFormUIState.isBundleDevops) {
      _.merge(cofsPayload, getDevopsAdvancedSettings());
    } else if (signUpFormUIState.isOpsgenie) {
      _.merge(
        cofsPayload,
        getOpsgenieAdvancedSettings(signUpFormUIState.signupState.dataRegion)
      );
    }
    if (signUpFormUIState.isBundleBBJSW && bbWorkSpace === undefined) {
      _.merge(
        cofsPayload,
        getBitbucketSignupAction(
          bbWorkSpace === undefined,
          !payload?.isExistingSitePickerN2E ||
            (payload?.selectedSite?.products &&
              !payload?.selectedSite?.products.includes(
                ProductKeys.JIRA_SOFTWARE
              )) ||
            false
        )
      );
    }
    if (bitbucketPreferred) {
      _.merge(
        cofsPayload,
        getBitbucketAdvancedSettings(isStaging, payload?.isLoggedIn)
      );
    }
    if (signUpFormUIState.isStatuspage) {
      _.merge(cofsPayload, getStatuspageAdvancedSettings(isStaging, spProduct));
    }

    _.merge(cofsPayload, getBillingAdvancedSettings());

    // Pass in known Experiment Key to COFS to activate Standard Developer license
    if (devMode) {
      Object.assign(cofsPayload, {
        experiment: {
          experimentKey: DEVELOPER_EXPERIMENT_KEY
        }
      });
    }

    if (signUpFormUIState.hideUserSegmentation) {
      _.merge(cofsPayload, {
        advancedSettings: {
          additionalProvisioningData: {
            hideUserSegmentation: true
          }
        }
      });
    }

    trackFormSubmit({
      isLoggedIn: userInfo?.isLoggedIn,
      segment,
      newRelic
    });

    const solutionStatsEvent = signUpFormUIState.isBundleBBJSW
      ? createSolutionStatsEvent({
          createAnalyticsEvent,
          signUpFormUIState
        })
      : undefined;

    if (!userInfo?.isLoggedIn) {
      if (signUpFormUIState.n2nSignInRedirect !== '') {
        return window.location.assign(signUpFormUIState.n2nSignInRedirect);
      }
      const displayName = signUpFormUIState.signupState.displayName || '';
      const product = productKeys.map((p) => p.product).join(',');
      const userSignupProducts = productKeys.map(({ product, edition }) => ({
        productKey: product || '',
        edition: edition || ''
      }));
      const nextUrlParams = {
        accountName: payload.cloudName,
        product
      };
      if (teamType) {
        Object.assign(nextUrlParams, { team_type: teamType });
      }

      const url = nextUrl(nextUrlParams);
      const { email } = signUpFormUIState.signupState;
      const redirectTo = url(newUserSignupRedirectUrl || window.location.href);
      signUpFormUIState.setPollingLoaderShown(true);

      return stargateClient
        .userSignup({
          displayName,
          email: email as string,
          redirectTo,
          locale: intl?.locale || 'en',
          gRecaptchaResponse: gRecaptchaResponse || '',
          products: userSignupProducts
        })
        .then((userSignupResponse: UserSignupResponse) => {
          const { otpRedirect } = userSignupResponse;
          sessionStorage.setItem(
            'r.e.d',
            Base64.encode(JSON.stringify({ email, displayName, redirectTo }))
          );
          if (otpRedirect) {
            window.location.assign(url(otpRedirect));
            return;
          }
          window.location.assign(url(checkEmailPageUrl));
        })
        .catch((err) => {
          console.log(err);
          signUpFormUIState.setSubmitError(SubmitErrorType.ERROR_TIMEOUT);
        });
    } else if (enableTintinMvp) {
      if (payload.products.length) {
        const ccpProducts = payload.products
          .map(({ product, edition }) => {
            // apps and product bundles SHIPit-55 SHPLV-364, we dont want it to try to look up app keys
            if (Object.values(AppKey).includes(product as AppKey)) {
              return;
            }

            return offeringCatalog.mapProductKeyAndEditionToCcpProduct({
              productKey: product as ProductKeys,
              edition: edition as string
            });
          })
          .filter((p) => p !== undefined);

        const siteName =
          (signUpFormUIState.signupState.cloudName as string) ||
          (deferredSiteName.isEnabled ? generatedCloudName : '') ||
          (signUpFormUIState.isBitbucket && workspaceID ? workspaceID : '');

        // stop if sitename is invalid • BXPSU-2541
        if (
          !deferredSiteName.isEnabled &&
          !signUpFormUIState.isValidSiteName &&
          !signUpFormUIState.isBitbucket
        ) {
          setFormReset((formReset) => !formReset);
          return;
        }

        signUpFormUIState.setPollingLoaderShown(true);

        // create the org if the user does not have a selected org
        let provisioningOrgId;
        let bitbucketWorkspaceMetadata = {};
        let bitbucketWorkspaceUuid = '';
        if (signUpFormUIState.isBitbucket) {
          // check if the workspaceSlug is available before provisioning
          let workspaceAri = generateBitbucketWorkspaceId();
          try {
            workspaceAri = await checkBitbucketWorkspaceSlugAvailability({
              workspaceSlug: workspaceID as string,
              stargateClient
            });
          } catch (error) {
            const errorCode = error?.response?.status;

            // We will only handle 409 and 400 codes.
            // Continue provisioning if we get an unexpected error response
            if (errorCode === 409 || errorCode === 400) {
              if (errorCode === 409) {
                signUpFormUIState.setWorkspaceIDTakenError(true);
              } else {
                signUpFormUIState.setIsValidWorkspaceID(false);
              }

              signUpFormUIState.setPollingLoaderShown(false);
              signUpFormUIState.setOrgProvisioningProgressMessage(false);
              setFormReset((formReset) => !formReset);
              return;
            }
          }

          provisioningOrgId =
            signUpFormUIState.selectedOrg?.id ??
            (await handleOrgCreate({
              orgDisplayName: workspaceID as string,
              stargateClient,
              createAnalyticsEvent
            }));

          if (!provisioningOrgId) {
            // handle org create failure
            signUpFormUIState.setSubmitError(SubmitErrorType.ERROR_ORG_CREATE);
            signUpFormUIState.setPollingLoaderShown(false);
            signUpFormUIState.setOrgProvisioningProgressMessage(false);
            setFormReset((formReset) => !formReset);
            return;
          }

          bitbucketWorkspaceUuid = workspaceAri.slice(
            workspaceAri.lastIndexOf('/') + 1
          );

          bitbucketWorkspaceMetadata = {
            orgId: provisioningOrgId,
            workspaceId: workspaceAri
          };
        }

        trackDeferSitenameSignups({
          isSiteNew: !signUpFormUIState.signupState.isExistingSitePickerN2E,
          isSitenameSpecified: !!signUpFormUIState.signupState?.cloudName,
          numberOfExistingUserSites: signUpFormUIState.pickableSites.length,
          optionalSitenameFeatureCohort: getDeferSitenameExperimentCohort(),
          sitename: siteName,
          userId: signUpFormUIState?.signUpFormProps?.userInfo?.accountId || ''
        });

        const MAX_RETRIES = 1;
        const RETRY_INTERVAL_SECONDS = 10;
        let activateProductRetries = 0;
        const callActivateProduct: () => Promise<void> = () =>
          stargateClient
            .activateProduct({
              email: signUpFormUIState.signupState.email as string,
              displayName: signUpFormUIState.signupState.displayName as string,
              // @ts-ignore
              ccpProducts,
              siteName,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              useCcpPlatform,
              consent: payload.consent,
              platform,
              signupContext: signupContextString,
              advancedSettings: cofsPayload.advancedSettings,
              isNewUser,
              gRecaptchaResponse: gRecaptchaResponse || '',
              ...(isTestTenant && { testingPurposes: true }),
              experiment: devMode
                ? { experimentKey: DEVELOPER_EXPERIMENT_KEY }
                : undefined,
              migrationId: signUpFormUIState.signupState.migrationId as string,
              ...bitbucketWorkspaceMetadata
            })
            .then((res) => {
              // **NOTE**  With rollout integration, the response may be a COFS response, provisionRequestId may not exist, see below
              const requestId = res.metadata?.provisionRequestId;
              const products = payload.products
                ?.map(({ product }) => product)
                .join(',');

              if (res.service === 'ccp') {
                // Route user to CCP Confirmation page
                let redirectUrl = `${confirmationPageUrl}?platform=ccp&provisioningRequestId=${requestId}&ondemandurl=${siteName}&products=${products}`;

                if (res.cloudId) {
                  redirectUrl += `&cloudId=${res.cloudId}`;
                }

                if (signupSource === 'confluenceBundle') {
                  redirectUrl += `&signupSource=${signupSource}`;
                }

                // Add `n2n` param for EXPCEN-3749
                if (
                  products.includes(ProductKeys.JIRA_SOFTWARE) &&
                  !!signUpFormUIState.signupState.isNewUser
                ) {
                  redirectUrl += `&n2n=true`;
                }

                // Add `templateId` param for EXPCEN-3749
                if (templateId) {
                  redirectUrl += `&templateId=${templateId}`;
                }

                //Add teamType for SPICE-931
                if (teamType) {
                  redirectUrl += `&team_type=${teamType}`;
                }

                window.location.assign(redirectUrl);
              } else {
                // Use older COFS logic to poll for successful status,
                // which then routes to confirmation page

                const progressUri = res.progressUri as string;

                // **NOTE*** maybe want a track event here if progressUri doesn't exist?

                pollForSuccess({
                  isLoggedIn: true,
                  confirmationPageUrl,
                  checkEmailPageUrl,
                  progressUri,
                  products: payload.products as ProductPayloadObject[],
                  analyticsTrack,
                  isStaging,
                  wacEnvUrl,
                  cloudName: siteName,
                  setSubmitError: signUpFormUIState.setSubmitError,
                  setPollingLoaderShown:
                    signUpFormUIState.setPollingLoaderShown,
                  isJswIncluded,
                  isNewUser,
                  isOpsgenieIncluded: signUpFormUIState.isOpsgenie,
                  dataRegion: signUpFormUIState.signupState.dataRegion,
                  resendEmail: undefined, // **NOTE** used for Devops signups that won't work in CCP yet
                  solutionStatsEvent,
                  hideUserSegmentation: false, // **NOTE** used for Jira Templates which I dont think will work in CCP yet
                  isAppProvisioningEligible, // **NOTE** won't work in CCP yet
                  isBitbucketWorkspace: signUpFormUIState.isBitbucket,
                  handlePollProgressStatusFailure: () =>
                    setFormReset((formReset) => !formReset),
                  ...(bitbucketWorkspaceUuid
                    ? { bitbucketWorkspaceUuid }
                    : undefined)
                });
              }
            })
            .catch(async (error) => {
              // **NOTE** We probably want a gas event so we can track these?
              console.log(error);

              const errorCode = error?.response?.status;
              const error_id = error?.response?.headers?.get('atl-traceid');

              if (errorCode === 403 && signUpFormUIState.isBitbucket) {
                if (activateProductRetries < MAX_RETRIES) {
                  await new Promise((res) =>
                    setTimeout(res, RETRY_INTERVAL_SECONDS * 1000)
                  );
                  activateProductRetries++;
                  return callActivateProduct();
                }
              }

              const redirectUrlShadowIt =
                errorCode === 403 && getRedirectUrlFromShadowItError(error);

              if (signUpFormUIState.isBitbucket) {
                trackBitbucketWorkspaceProvFailure({
                  createAnalyticsEvent,
                  metadata: {
                    errorCode: errorCode,
                    workspaceUuid: workspaceUuid
                  }
                });
              }

              if (errorCode === 409) {
                if (signUpFormUIState.isBitbucket) {
                  signUpFormUIState.setWorkspaceIDTakenError(true);
                }
                signUpFormUIState.setActivateProductError(true);
                signUpFormUIState.setPollingLoaderShown(false);
                signUpFormUIState.setOrgProvisioningProgressMessage(false);

                setFormReset((formReset) => !formReset);
              } else if (errorCode === 403 && !!redirectUrlShadowIt) {
                // Shadow IT unauthorized user redirects to adminhub's Shadow IT request flow.
                window.location.assign(redirectUrlShadowIt);
              } else {
                if (signUpFormUIState.isBitbucket) {
                  signUpFormUIState.setPollingLoaderShown(false);
                  setFormReset((formReset) => !formReset);
                }
                signUpFormUIState.setSubmitError(
                  SubmitErrorIDType.ERROR_TIMEOUT(error_id)
                );
              }
            });

        return callActivateProduct();
      }
    }

    return formSubmitN2N({
      payload: cofsPayload,
      wacEnvUrl,
      confirmationPageUrl,
      checkEmailPageUrl,
      isStaging,
      solutionStatsEvent,
      isLoggedIn: userInfo?.isLoggedIn,
      isOpsgenieIncluded: signUpFormUIState.isOpsgenie,
      dataRegion: payload.dataRegion,
      setPollingLoaderShown: signUpFormUIState.setPollingLoaderShown,
      analyticsTrack,
      setSubmitError: signUpFormUIState.setSubmitError,
      resendEmail: signUpFormUIState.resendEmail,
      hideUserSegmentation: signUpFormUIState.hideUserSegmentation,
      isAppProvisioningEligible
    });
  };

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmitWithRecaptcha = async (
    handleSubmitProps: HandleSubmitType
  ) => {
    if (!executeRecaptcha) {
      return;
    }

    let token: string;

    try {
      token = await executeRecaptcha('userSignup');
    } catch (error) {
      trackRecaptchaError({ segment });
      throw error;
    }

    if (token) {
      trackSubmitWithCaptcha({ newRelic });

      try {
        await handleSubmit({
          ...handleSubmitProps,
          gRecaptchaResponse: token
        });

        trackSignUpFormSubmitSucceeded({
          createAnalyticsEvent
        });
      } catch (error) {
        trackSignUpFormSubmitFailed({
          createAnalyticsEvent
        });
      }
    }
  };

  const handleStartN2E = async (
    products: Product[],
    site: Sites,
    migrationId: string | null
  ) => {
    if (products.length && site.cloudId && site.url) {
      const siteDomain = site.url.replace('https://', '');

      // SHIPIt-55 SHPLV-364
      const productsIncludesApps = products
        .map(({ product }) => {
          return Object.values(AppKey).includes(product as AppKey);
        })
        .includes(true);

      if (provisionSecondPartyApps && productsIncludesApps) {
        // @ts-ignore
        const appKey = products.find(({ product }) => {
          return Object.values(AppKey).includes(product as AppKey);
        }).product;
        const xsrfToken = await bxpExpressClient.getXsrfToken();
        const siteName = site.url.replace('https://', '').split('.')[0];
        await wrappedClientReq({
          siteName,
          // @ts-ignore
          appKey,
          xsrfToken
        });
      }

      if (products.length > 1) {
        const { cloudId } = site;
        const siteName = siteDomain.split('.')[0];
        const productString = context.productsOffered.join(',');

        const signupContext = {};
        const bxpGatewayAnchorCookie = Cookies.get('bxp_gateway_anchor') || '';
        if (bxpGatewayAnchorCookie) {
          try {
            const bxpGatewayAnchor = JSON.parse(bxpGatewayAnchorCookie);
            if (bxpGatewayAnchor) {
              bxpGatewayAnchor.timestamp = new Date();
              Object.assign(signupContext, { bxpGatewayAnchor });
            }
          } catch (error) {
            createAnalyticsEvent({
              eventName: 'bxp.anchorJourney.parseCookieError'
            }).fire();
          }
        }

        const productsEditions =
          products.map(({ product, edition }) => {
            return offeringCatalog.mapProductKeyAndEditionToCcpProduct({
              productKey: product as ProductKeys,
              edition: edition as string
            });
          }) ?? [];

        const offeringKeys = productsEditions
          .map((item) => item.offeringId)
          .join(',');

        const ccpHamsStatus = await stargateClient.getCcpHamsStatus({
          offeringKeys,
          cloudId
        });

        if (ccpHamsStatus.service === 'CCP') {
          const ccp = await stargateClient.getLicenceInformationStatus({
            cloudId
          });
          const primaryProductCCP = Object.values(ccp.products ?? {}).find(
            (prod) => !!prod.ccpTransactionAccountId && !!prod.ccpEntitlementId
          );

          if (primaryProductCCP) {
            const res = await Promise.all(
              productsEditions.map(async (product) => {
                return await stargateClient.postProductActivate({
                  offerings: [product],
                  transactionAccountId:
                    primaryProductCCP.ccpTransactionAccountId,
                  entitlementId: primaryProductCCP.ccpEntitlementId || '',
                  cloudId,
                  siteName,
                  advancedSettings: {
                    signupContext: JSON.stringify(signupContext)
                  },
                  webPlatform: 'magnolia'
                });
              })
            );

            if (res[0].progressUri) {
              const provisioningRequestIds = res
                .map((product) => product.progressUri.split('/').pop())
                .join(',');

              let redirectUrl = `${confirmationPageUrl}?platform=ccp&provisioningRequestId=${provisioningRequestIds}&ondemandurl=${siteName}&products=${productString}`;

              if (signupSource === 'confluenceBundle') {
                redirectUrl += `&signupSource=${signupSource}`;
              }

              window.location.assign(redirectUrl);
            }
          } else {
            const discoverProductsUrl = `${site.url}/admin/billing/applications`;
            window.location.href = discoverProductsUrl;
          }
        } else {
          const newProducts: ProductPayloadObject[] = products.map((item) => ({
            accountName: siteName,
            edition: item.edition,
            productKey: item.product
          }));
          const payload: FormPayloadActivateProducts = {
            cloudName: siteName,
            cloudId,
            activateProducts: true,
            products: newProducts,
            signupContext: JSON.stringify(signupContext)
          };

          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          const res: any = await stargateClient.postCloudSignUp({
            payload,
            webPlatform: 'magnolia'
          });

          if (res.progressUri) {
            const requestId = getRequestId(res?.progressUri);

            let redirectUrl = `${confirmationPageUrl}?ondemandurl=${siteName}&cloudId=${cloudId}&requestId=${requestId}&products=${productString}`;

            if (signupSource === 'confluenceBundle') {
              redirectUrl += `&signupSource=${signupSource}`;
            }

            window.location.assign(redirectUrl);
          } else {
            const discoverProductsUrl = `${site.url}/admin/billing/applications`;
            window.location.href = discoverProductsUrl;
          }
        }

        return;
      } else {
        const baseStartUrl = isStaging
          ? 'https://start.stg.atlassian.com'
          : 'https://start.atlassian.com';
        const productKey = products[0].product;

        let startUrl = `${baseStartUrl}?startTrialProduct=${productKey}&startTrialCloudId=${site.cloudId}&startTrialInstance=${siteDomain}`;
        if (editionOverride) {
          startUrl += `&edition=${editionOverride}`;
        } else if (products[0].edition) {
          startUrl += `&edition=${products[0].edition}`;
        }
        if (migrationId) {
          startUrl += `&migrationSourceUuid=${migrationId}`;
        }

        // custom signup context passed in from CMS configured Signup Form
        if (customSignupContext) {
          const sanitizedCustomSignupContext =
            sanitizeCustomSignupContext(customSignupContext);
          startUrl += `&customizedContextData=${sanitizedCustomSignupContext}`;
        }

        const queryParameters = Url.getUrlParams() || {};
        if (queryParameters.hasOwnProperty('billingMode')) {
          startUrl += `&billingMode=${queryParameters.billingMode}`;
        }
        if (queryParameters.hasOwnProperty('billingCode')) {
          startUrl += `&billingCode=${queryParameters.billingCode}`;
        }
        startUrl = signUpFormUIState?.signupOrigin?.addToUrl(startUrl);

        const idLoginStartUrl = new URL(
          Environment.getIdSigninUrl(undefined, false, startUrl)
        );
        idLoginStartUrl.searchParams.set('application', 'wac');
        idLoginStartUrl.searchParams.set(
          'login_hint',
          signUpFormUIState.signupState.email as string
        );
        const idLoginStartUrlString = idLoginStartUrl.toString();

        trackN2ESubmit2({
          cloudId: site.cloudId,
          products,
          signupUrl: idLoginStartUrlString,
          accountId:
            signUpFormUIState?.signUpFormProps?.userInfo?.accountId || '',
          createAnalyticsEvent
        });

        window.location.href = idLoginStartUrlString;
      }
    } else {
      // console.error('Missing site or product data');
    }
  };

  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Verify user is logged into correct account
    if (userInfo?.isLoggedIn) {
      const userInfoCheck = await stargateClient.me({});
      if (userInfoCheck && userInfo?.accountId !== userInfoCheck.accountId) {
        signUpFormUIState.setSubmitError(SubmitErrorType.AAID_MISMATCH);
        return;
      }
    }

    const buttonLabel = !signUpFormUIState.signupState.isExistingSitePickerN2E
      ? signUpFormUIState.buttonText.defaultMessage
      : signUpFormUIState.signupState.isSubmitting
      ? messages.submitProcessingLabel.defaultMessage
      : signUpFormUIState.startButtonText.defaultMessage;

    trackSignupButtonClicked({ context, buttonLabel, createAnalyticsEvent });

    const cloudId = signUpFormUIState.signupState?.selectedSite?.cloudId;
    const siteName = signUpFormUIState.signupState?.cloudName;

    // Check shadow IT to redirect user or not, for both N2N or N2E
    if (userInfo?.isLoggedIn) {
      try {
        if (siteName) {
          // Check for Shadow IT authorization.
          await stargateClient.getActivationAuthorization({
            productKeys: signUpFormUIState.signupState.products
              .filter(isNotUndefined)
              .map(({ product }) => product as ProductKeys),
            productEdition: signUpFormUIState.signupState.products.filter(
              (product) => !!product && !!product.edition
            )[0].edition as string,
            cloudId,
            siteName,
            webPlatform: 'magnolia'
          });

          createAnalyticsEvent({
            event: 'authorized',
            eventName: 'cloudSignupShadowItAuthorized',
            eventType: 'track',
            eventComponent: 'cloudSignupShadowIt',
            eventColumn: 'cloudSignupShadowIt'
          }).fire();
        }
      } catch (error) {
        console.log('Error during shadow IT', error);

        // Unauthorized due to Shadow IT.
        if (isResponseError(error) && error.response.status === 403) {
          createAnalyticsEvent({
            event: 'unauthorized',
            eventName: 'cloudSignupShadowItUnauthorized',
            eventType: 'track',
            eventComponent: 'cloudSignupShadowIt',
            eventColumn: 'cloudSignupShadowIt'
          }).fire();

          const redirectUrl = getRedirectUrlFromShadowItError(error);

          // Silently fail errors, unless explicitly unauthorized w/ redirectUrl.
          if (!!redirectUrl) {
            createAnalyticsEvent({
              event: 'redirect',
              eventName: 'cloudSignupShadowItRedirect',
              eventType: 'track',
              eventComponent: 'cloudSignupShadowIt',
              eventColumn: 'cloudSignupShadowIt'
            }).fire();
            // Unauthorized redirect to adminhub's Shadow IT request flow.
            window.location.assign(redirectUrl);
            return;
          }
        }
        // Silently fail unexpected errors for Shadow IT.
        createAnalyticsEvent({
          event: 'unexpected error',
          eventName: 'cloudSignupShadowItError',
          eventType: 'track',
          eventComponent: 'cloudSignupShadowIt',
          eventColumn: 'cloudSignupShadowIt'
        }).fire();
        console.warn('Unexpected error during Shadow IT', error);
      }
    }

    if (
      signUpFormUIState.signupState.isExistingSitePickerN2E &&
      signUpFormUIState.isBundleBBJSW
    ) {
      // check to get edition type so existing site doesnt downgrade
      const productEdition = await checkProductEdition({
        cloudId,
        productKey: ProductKeys.JIRA_SOFTWARE,
        wacEnvUrl,
        createAnalyticsEvent
      });

      return handleSubmitWithRecaptcha({
        payload: signUpFormUIState.signupState,
        activateProducts: true,
        cloudId,
        isJswIncluded: findIfJswIncluded(cloudId),
        productEdition
      });
    } else if (
      signUpFormUIState.signupState.isExistingSitePickerN2E &&
      signUpFormUIState.signupState.selectedSite
    ) {
      trackN2ESubmit({
        cloudId,
        products: signUpFormUIState.signupState.products,
        segment
      });

      return handleStartN2E(
        signUpFormUIState.signupState.products,
        signUpFormUIState.signupState.selectedSite,
        signUpFormUIState.signupState.migrationId
      );
    } else {
      if (!userInfo?.isLoggedIn) {
        // For e2e testing, allow bypassing captcha on known pattern
        // Must be handled in the backend as well
        if (
          signUpFormUIState.signupState.email &&
          enableSkipRecaptcha &&
          shouldSkipRecaptcha(signUpFormUIState.signupState.email)
        ) {
          return handleSubmitWithRecaptcha({
            payload: signUpFormUIState.signupState
          });
        }
        return handleSubmitWithRecaptcha({
          payload: signUpFormUIState.signupState
        });
      } else {
        return handleSubmitWithRecaptcha({
          payload: signUpFormUIState.signupState
        });
      }
    }
  };

  const handleDeferSite = () => {
    if (signUpFormUIState.isBitbucket) return null;

    const siteNameField = (!signUpFormUIState.signupState
      .isExistingSitePickerN2E ||
      signUpFormUIState.isSitePickerDisabled) && (
      <SiteNameField
        setExposedToDeferSitename={setExposedToDeferSitename}
        onSiteNameChange={(cloudName, cloudNameLength) => {
          signUpFormUIState.setSignupState({
            ...signUpFormUIState.signupState,
            cloudName,
            cloudNameLength
          });
        }}
        onSiteNameUseExisting={() => {
          formExistingSiteShown(true);
          segment('bxp.formCloudSignup.useExistingSiteClicked', {});
          signUpFormUIState.setSignupState({
            ...signUpFormUIState.signupState,
            isExistingSitePickerN2E: true,
            selectedSite: null,
            cloudName: ''
          });
          signUpFormUIState.setIsValidSiteName(false);
        }}
        key={wacEnvUrl}
        sites={null}
        shouldShowUseExisting={
          signUpFormUIState.shouldShowUseExisting &&
          !signUpFormUIState.isSitePickerDisabled
        }
        siteNameAfter={signUpFormUIState.siteNameAfter}
        wacEnvUrl={wacEnvUrl}
        analyticsTrack={analyticsTrack}
        setIsValidSiteName={(val) => signUpFormUIState.setIsValidSiteName(val)}
        outlineOnFocus={outlineOnFocus}
        popOverError={popOverError}
        isDisabled={signUpFormUIState.signupState.isSubmitting}
        fadeOnDisable={fadeOnDisable}
        showWarningIcon={showWarningIcon}
        isSuiteSignup={signUpFormUIState.isBundleDevops}
        isDeferredSiteNameEnabled={deferredSiteName.isEnabled}
        activateProductError={signUpFormUIState.activateProductError}
        setActivateProductError={signUpFormUIState.setActivateProductError}
        siteNameGenerationExperiment={
          site_name_generation && isNewUserInJiraOrConfluenceNotPaid
        }
        siteNameAddtionalCopyExperiment={
          site_name_additional_copy && isNewUserInJiraOrConfluenceNotPaid
        }
        recommendedSiteName={recommendedSiteName}
      />
    );

    const sitePickerField = !signUpFormUIState.isSitePickerDisabled &&
      signUpFormUIState.signupState.isExistingSitePickerN2E &&
      userInfo &&
      signUpFormUIState.pickableSites.length > 0 && (
        <SitePickerField
          key="site-picker"
          onSiteNameChange={(selectedSite, cloudName) => {
            signUpFormUIState.setSignupState({
              ...signUpFormUIState.signupState,
              selectedSite,
              cloudName
            });
          }}
          onSiteNameStartNew={() => {
            formExistingSiteShown(false);
            segment('bxp.formCloudSignup.startANewSite', {});
            signUpFormUIState.setSignupState({
              ...signUpFormUIState.signupState,
              isExistingSitePickerN2E: false,
              selectedSite: null,
              cloudName: ''
            });
          }}
          isBundleActive={signUpFormUIState.isBundleBBJSW}
          sites={signUpFormUIState.pickableSites}
          siteNameAfter={signUpFormUIState.siteNameAfter}
          analyticsTrack={analyticsTrack}
          isFree={signUpFormUIState.isFree}
          setIsValidSiteName={(val: boolean) =>
            signUpFormUIState.setIsValidSiteName(val)
          }
          isDisabled={signUpFormUIState.signupState.isSubmitting}
          showFreeLimitExceededError={
            signUpFormUIState.selectedSiteIsInBreachOfFree
          }
          selectedSiteId={selectedSiteId}
          productKeys={signUpFormUIState.signUpFormProps.productKeys}
          isDeferredSiteNameEnabled={deferredSiteName.isEnabled}
          fadeOnDisable={fadeOnDisable}
        />
      );

    const isBeaconLimited =
      signUpFormUIState.isBeacon && signUpFormUIState.pickableSites.length <= 0;

    const beaconInfoBox = (
      <InfoBox>
        <p>
          <FormattedMessage {...messages.beaconRequireProducts} />
        </p>
        <Button
          appearance="primary"
          spacing="compact"
          href={`${wacEnvUrl}/software`}
        >
          <FormattedMessage {...messages.beaconExplore} />
        </Button>
      </InfoBox>
    );

    if (deferSiteNameCreation) {
      if (signUpFormUIState.signupState.isLoggedIn) {
        return [siteNameField, sitePickerField];
      } else {
        return null;
      }
    } else {
      if (isBeaconLimited) {
        return [beaconInfoBox];
      } else {
        return [siteNameField, sitePickerField];
      }
    }
  };

  if (signUpFormUIState.showResendEmail) {
    const baseUrl = isStaging
      ? `${wacEnvUrl}/apis/stg/hamlet`
      : `${wacEnvUrl}/hamlet`;
    const resendEmailScreen = (
      <CheckEmailPage
        layout="devops"
        baseUrl={baseUrl}
        analyticsTrack={analyticsTrack}
        hideHeader={
          signUpFormUIState.isBundleBBJSW || signUpFormUIState.isBundleDevops
        }
        productKeys={productKeys.map((p) => (p.product ? p.product : ''))}
      />
    );
    return <ResendEmailContainer>{resendEmailScreen}</ResendEmailContainer>;
  }
  if (signUpFormUIState.showDevopsCarouselEnd) {
    return (
      <CarouselEnd
        isBundleDevops={signUpFormUIState.isBundleDevops}
        goToProduct={signUpFormUIState.goToURL}
      />
    );
  }

  const [workspaceID, setWorkspaceID] = useState<string | undefined>(undefined);

  const { orgList } = signUpFormUIState.orgFetchResponse;

  const showWorkspaceIDErrorMessage =
    workspaceID && !signUpFormUIState.isValidWorkspaceID;

  const workspaceIdIsValid = (id: string | undefined): boolean => {
    return !!id && WORKSPACE_VALIDATION_PATTERN.test(id);
  };

  const cmtForCCPFeatureFlag = useFeatureGate('wac-enable-cmt-for-ccp-site');
  const enableSignUp =
    !signUpFormUIState.signupState.isNewCCPSiteForMigration &&
    (cmtForCCPFeatureFlag || !signUpFormUIState.signupState.hasCCPEntitlements);

  return (
    <WacSignupFormContainer
      onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
        if (
          (signUpFormUIState.orgAdminVortexStatusCounts.ENABLED === 0 &&
            signUpFormUIState.orgAdminVortexStatusCounts.DISABLED > 0) ||
          (orgList && orgList.length === 0)
        ) {
          signUpFormUIState.setOrgProvisioningProgressMessage(true);
        }
        if (
          signUpFormUIState.isConfluence &&
          !signUpFormUIState.isValidChooseSignup
        ) {
          setShowError(true);
          setIsFocusedChooseSignup(false);
          event.preventDefault();
        } else {
          signUpFormUIState.setSignupState({
            ...signUpFormUIState.signupState,
            isSubmitting: true
          });
          submitHandler(event)
            .then(() => {
              trackSignUpFormSubmitSucceeded({
                createAnalyticsEvent
              });
            })
            .catch(() => {
              trackSignUpFormSubmitFailed({
                createAnalyticsEvent
              });
            });
        }
      }}
      method="POST"
      className={signUpFormUIState.hideAsterisks ? 'no-asterisks' : ''}
    >
      {isNewUserInSiteNameExperiment && (
        <InputSiteNameMessage>
          <FormattedMessage {...messages.inputSiteNameMessage} />
        </InputSiteNameMessage>
      )}
      {!hideGreeting && (
        <WelcomeDiv
          headerStyle={
            signUpFormUIState.isBundleDevops ||
            (signUpFormUIState.isBundleBBJSW && !userInfo?.isLoggedIn)
          }
        >
          <FormWelcomeMessage
            isBundleDevops={signUpFormUIState.isBundleDevops}
            isLoggedIn={userInfo?.isLoggedIn}
            welcomeMessage={signUpFormUIState.welcomeMessage}
            username={signUpFormUIState.initialState.firstName}
          />
        </WelcomeDiv>
      )}
      {
        // show email for all non-Devops forms, or Devops N2N
        ((showEmail && !signUpFormUIState.isBundleDevops) ||
          (showEmail &&
            signUpFormUIState.isBundleDevops &&
            !userInfo?.isLoggedIn)) &&
          !isNewUserInSiteNameExperiment && (
            <EmailField
              onEmailChange={(email) => {
                signUpFormUIState.setSignupState({
                  ...signUpFormUIState.signupState,
                  email
                });
              }}
              initialEmail={signUpFormUIState.intialEmail}
              signInAccountUrl={signInAccountUrl}
              switchAccountUrl={switchAccountUrl}
              originAnalyticsAttributes={originAnalyticsAttributes}
              analyticsTrack={analyticsTrack}
              wacEnvUrl={wacEnvUrl}
              isStaging={isStaging}
              isDisabled={signUpFormUIState.signupState.isSubmitting}
              outlineOnFocus={outlineOnFocus}
              popOverError={popOverError}
              fadeOnDisable={fadeOnDisable}
              showWarningIcon={showWarningIcon}
              onSSOCheckValid={(isValid: boolean) => {
                signUpFormUIState.setSignupState({
                  ...signUpFormUIState.signupState,
                  isSSOValid: isValid
                });
              }}
              isDeferredSiteNameEnabled={deferredSiteName.isEnabled}
              isFocusedChooseSignup={isFocusedChooseSignup}
              setIsFocusedChooseSignup={setIsFocusedChooseSignup}
              showError={showError}
              setShowError={setShowError}
              setN2NSignInRedirect={signUpFormUIState.setN2NSignInRedirect}
              isConfluence={signUpFormUIState.isConfluence}
            />
          )
      }
      {(!signUpFormUIState.isBundleBBJSW || !userInfo?.isLoggedIn) &&
        !signUpFormUIState.signupState.isExistingSitePickerN2E &&
        !hideFullName &&
        !enableDeferredFullName &&
        !enableDefaultExpandedEmail &&
        !signUpFormUIState.isConfluence && (
          <DisplayNameField
            displayname={signUpFormUIState.signupState.displayName}
            onNameChange={(displayName) =>
              signUpFormUIState.setSignupState({
                ...signUpFormUIState.signupState,
                displayName
              })
            }
            outlineOnFocus={outlineOnFocus}
            popOverError={popOverError}
            isDisabled={signUpFormUIState.signupState.isSubmitting}
            fadeOnDisable={fadeOnDisable}
          />
        )}
      {signUpFormUIState.isOpsgenie &&
        !signUpFormUIState.isBundleDevops &&
        !signUpFormUIState.signupState.isExistingSitePickerN2E &&
        !!userInfo?.isLoggedIn && (
          <GdprLocation
            onLocationChange={(dataRegion) =>
              signUpFormUIState.setSignupState({
                ...signUpFormUIState.signupState,
                dataRegion
              })
            }
            analyticsTrack={analyticsTrack}
          />
        )}
      {signUpFormUIState.isBundleBBJSW && (
        <BitbucketJiraInfoBox
          productsExisting={signUpFormUIState.productsExisting}
          analyticsTrack={analyticsTrack}
          bitbucketWorkspaces={bitbucketWorkspaces}
        />
      )}
      {signUpFormUIState.isBitbucket && (
        <BitbucketFieldsContainer>
          <TextFieldWrapper
            error={
              showWorkspaceIDErrorMessage ||
              signUpFormUIState.workspaceIDTakenError
            }
          >
            <FieldLabel>
              <FormattedMessage
                {...messages.bitbucketWorkspaceIdFieldLabelMessage}
              />
            </FieldLabel>
            <TextField
              testId="bitbucket-workspace-id-input"
              autoComplete="off"
              maxLength={62}
              elemBeforeInput={<WorkspaceIdPrefix children="bitbucket.org/" />}
              value={workspaceID}
              onChange={({
                target: { value: workspaceId }
              }: React.ChangeEvent<HTMLInputElement>) => {
                setWorkspaceID(workspaceId);
                if (
                  !signUpFormUIState.isValidWorkspaceID &&
                  workspaceIdIsValid(workspaceId)
                ) {
                  signUpFormUIState.setIsValidWorkspaceID(true);
                } else if (!workspaceIdIsValid(workspaceId)) {
                  signUpFormUIState.setIsValidWorkspaceID(false);
                }

                if (signUpFormUIState.workspaceIDTakenError)
                  signUpFormUIState.setWorkspaceIDTakenError(false);
              }}
              isRequired={true}
              isInvalid={workspaceID && !signUpFormUIState.isValidWorkspaceID}
            />
            {showWorkspaceIDErrorMessage && (
              <ErrorMessageWithDefaultClassName>
                <FormattedMessage
                  {...messages.bitbucketWorkspaceIdFieldErrorMessageCMT}
                />
              </ErrorMessageWithDefaultClassName>
            )}
          </TextFieldWrapper>
          {signUpFormUIState.workspaceIDTakenError && (
            <ErrorMessageWithDefaultClassName>
              <FormattedMessage
                {...messages.bitbucketWorkspaceIdTakenErrorMessageCMT}
              />
            </ErrorMessageWithDefaultClassName>
          )}
          {!showWorkspaceIDErrorMessage &&
            !signUpFormUIState.workspaceIDTakenError && (
              <HelperMessageContainer>
                <FormattedMessage
                  {...messages.bitbucketWorkspaceIdFieldHelperMessage}
                />
              </HelperMessageContainer>
            )}
          {orgList &&
            shouldRenderBitbucketOrgPicker(
              signUpFormUIState.orgAdminVortexStatusCounts
            ) && (
              <BitbucketOrganizationPicker
                orgList={orgList}
                selectedOrg={signUpFormUIState.selectedOrg}
                setSelectedOrg={signUpFormUIState.setSelectedOrg}
              />
            )}
          {orgList &&
            signUpFormUIState.orgAdminVortexStatusCounts.ENABLED > 0 &&
            signUpFormUIState.orgAdminVortexStatusCounts.DISABLED > 0 && (
              <OrgPickerElementMargin>
                <InfoBox>
                  <p>
                    <SanitizedFormattedHtmlMessage
                      {...messages.bitbucketMissingOrgsInfoMessage}
                      values={{
                        supportUrl: 'https://support.atlassian.com/contact/'
                      }}
                    />
                  </p>
                </InfoBox>
              </OrgPickerElementMargin>
            )}
          {orgList &&
            signUpFormUIState.orgAdminVortexStatusCounts.ENABLED === 0 &&
            signUpFormUIState.orgAdminVortexStatusCounts.DISABLED > 0 && (
              <OrgPickerElementMargin>
                <InfoBox>
                  <p>
                    <SanitizedFormattedHtmlMessage
                      {...messages.bitbucketCreateNewOrgInfoMessage}
                      values={{
                        supportUrl: 'https://support.atlassian.com/contact/'
                      }}
                    />
                  </p>
                </InfoBox>
              </OrgPickerElementMargin>
            )}
        </BitbucketFieldsContainer>
      )}
      <>{handleDeferSite()}</>
      {signUpFormUIState.selectedSiteIsInBreachOfFree &&
        signUpFormUIState.signupState.isExistingSitePickerN2E && (
          <BreachInfoBox
            analyticsTrack={analyticsTrack}
            selectedSiteId={
              signUpFormUIState.signupState.selectedSite?.cloudId || ''
            }
          />
        )}
      {signUpFormUIState.isBundleBBJSW &&
        signUpFormUIState.bbWorkspaces.length > 0 && (
          <BitbucketAccountPicker
            sitesCount={signUpFormUIState.pickableSites.length}
            bbWorkspaces={signUpFormUIState.bbWorkspaces}
            bbWorkspacesTotal={signUpFormUIState.bbWorkspacesTotal}
            selectedSite={
              signUpFormUIState.signupState.selectedSite?.displayName ||
              signUpFormUIState.signupState.cloudName
            }
            bbAccountCopy={(isEnabled) =>
              signUpFormUIState.setIsBbCopyEnabled(isEnabled)
            }
            onAccountChange={(bbName) =>
              signUpFormUIState.setSignupState({
                ...signUpFormUIState.signupState,
                bbName
              })
            }
            bbNameOverride={signUpFormUIState.signupState.bbName || ''}
            analyticsTrack={analyticsTrack}
            isValidSiteName={signUpFormUIState.isValidSiteName}
            isDisabled={signUpFormUIState.signupState.isSubmitting}
          />
        )}
      {enableSignUp && marketingConsentRequired && (
        <MarketingConsent className="marketing-consent sign-up-field-container">
          <Checkbox
            label={<FormattedMessage {...messages.marketingConsent} />}
            isDisabled={signUpFormUIState.signupState.isSubmitting}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const consentsState = signUpFormUIState.signupState;
              consentsState.consent.consents[2].granted = event.target.checked;
              signUpFormUIState.setSignupState({ ...consentsState });
            }}
          />
        </MarketingConsent>
      )}
      {signUpFormUIState.submitError !== SubmitErrorType.NO_ERROR && (
        <ErrorMessageWithDefaultClassName>
          <SanitizedFormattedHtmlMessage
            {...getTranslatableError(signUpFormUIState.submitError)}
          />
        </ErrorMessageWithDefaultClassName>
      )}
      {enableSignUp &&
        !signUpFormUIState.isBundleDevops &&
        !isNewUserInSiteNameExperiment && (
          <>
            {enableDefaultExpandedEmail ? (
              <EulaDefaultExpandedEmail
                onClick={() => {
                  segment('bxp.formCloudSignup.eulaClicked', {});
                }}
              >
                <EulaComponent />
              </EulaDefaultExpandedEmail>
            ) : signUpFormUIState.isConfluence ? (
              <EulaChooseSignup
                onClick={() => {
                  segment('bxp.formCloudSignup.eulaClicked', {});
                }}
              >
                <SanitizedFormattedHtmlMessage {...messages.eulaChooseSignup} />
              </EulaChooseSignup>
            ) : (
              <Eula
                onClick={() => {
                  segment('bxp.formCloudSignup.eulaClicked', {});
                }}
              >
                <EulaComponent />
              </Eula>
            )}
          </>
        )}
      {!enableSignUp && (
        <CCPSiteMigrationContainer>
          <p>
            {!signUpFormUIState.signupState.isExistingSitePickerN2E ? (
              <FormattedMessage {...messages.ccpStartNewSiteRequiresSupport} />
            ) : (
              <FormattedMessage {...messages.ccpSiteMigrationRequiresSupport} />
            )}
            <br />
            <FormattedMessage {...messages.ccpSiteMigrationFollowSteps} />
            <b>
              <FormattedMessage {...messages.contactSupportForCCPSite} />
            </b>
            <FormattedMessage {...messages.ccpSiteMigrationFollowStepsBelow} />
          </p>
          <ul>
            <li style={{ marginBottom: 0 }}>
              <FormattedMessage {...messages.ccpSiteMigrationStepSelect} />
              <b>
                <FormattedMessage
                  {...messages.ccpSiteMigrationStepProductTrials}
                />
              </b>
              <FormattedMessage {...messages.ccpSiteMigrationStepChoose} />
              <b>
                <FormattedMessage
                  {...messages.ccpSiteMigrationStepExtendProductTrials}
                />
              </b>
            </li>
            <li style={{ marginTop: 0, marginBottom: 0 }}>
              <FormattedMessage {...messages.ccpSiteMigrationStepSelectThe} />
              <b>
                <FormattedMessage {...messages.ccpSiteMigrationStepSEN} />
              </b>
              <FormattedMessage {...messages.ccpSiteMigrationStepDCLicense} />
              <b>
                <FormattedMessage {...messages.ccpSiteMigrationStepHelp} />
              </b>
            </li>
            <li style={{ marginTop: 0, marginBottom: 0 }}>
              <FormattedMessage
                {...messages.ccpSiteMigrationStepAdditionalInfo}
                values={{
                  AdditionalDetailsFieldName: (
                    <b>
                      <FormattedMessage
                        {...messages.ccpSiteMigrationStepAdditionalDetailsFieldName}
                      />
                    </b>
                  )
                }}
              />
            </li>
          </ul>
          <br />
          <AKButton
            testId="contact-support-btn"
            style={{ width: '100%' }}
            appearance="primary"
            href={`${wacEnvUrl}/company/contact/purchasing-licensing#/`}
            target="_blank"
          >
            <FormattedMessage {...messages.contactSupportForCCPSite} />
          </AKButton>
        </CCPSiteMigrationContainer>
      )}
      {enableSignUp && !signUpFormUIState.signupState.isExistingSitePickerN2E && (
        <SubmitButton
          label={signUpFormUIState.buttonText.defaultMessage}
          className={`submit-button ${
            signUpFormUIState.isBundleDevops ? 'devops' : ''
          }`}
          id="submit-button-6707236536632157"
          isDisabled={
            signUpFormUIState.signupState.isSubmitting ||
            (!signUpFormUIState.isConfluence && !signUpFormUIState.isValid)
          }
          isLoading={signUpFormUIState.pollingLoaderShown}
          shouldFitContainer
          type="submit"
          appearance="primary"
          testId="submit-button"
        >
          <FormattedMessage
            {...signupButtonText}
            values={{ product: productName }}
          />
        </SubmitButton>
      )}
      {enableSignUp && signUpFormUIState.signupState.isExistingSitePickerN2E && (
        <SubmitButton
          label={signUpFormUIState.startButtonText.defaultMessage}
          className={`submit-button ${
            signUpFormUIState.isBundleDevops ? 'devops' : ''
          }`}
          id="submit-button-6707236536632157"
          isDisabled={
            !signUpFormUIState.isValidN2E ||
            signUpFormUIState.signupState.isSubmitting ||
            signUpFormUIState.selectedSiteIsInBreachOfFree ||
            signUpFormUIState.disableSignInWhileTestingFreeBreach
          }
          isLoading={signUpFormUIState.pollingLoaderShown}
          shouldFitContainer
          type="submit"
          appearance="primary"
          testId="submit-button"
        >
          <FormattedMessage
            {...(signUpFormUIState.signupState.isSubmitting
              ? messages.submitProcessingLabel
              : signUpFormUIState.startButtonText)}
          />
        </SubmitButton>
      )}
      {signUpFormUIState.isBundleDevops && (
        <Eula
          onClick={() => {
            segment('bxp.formCloudSignup.eulaClicked', {});
          }}
        >
          <SanitizedFormattedHtmlMessage {...messages.eulaDevops} />
        </Eula>
      )}
      {signUpFormUIState.orgProvisioningProgressMessage && (
        <OrgProvisioningProgressContainer>
          <FormattedMessage
            {...messages.bitbucketOrgCreationPreparingProductMessage}
          />
          <br />
          <FormattedMessage
            {...messages.bitbucketOrgCreationEstimatedTimeMessage}
          />
        </OrgProvisioningProgressContainer>
      )}
      {enableSignUp &&
        !allowSignupWithAllSocialOptions &&
        !signUpFormUIState.isConfluence && (
          <NoCard
            as={
              !signUpFormUIState.isConfluence && enableDefaultExpandedEmail
                ? NoCardDefaultExpandedEmail
                : NoCard
            }
          >
            {!isNewUserInSiteNameExperiment && (
              <FormattedMessage {...messages.noCard} />
            )}
          </NoCard>
        )}
      {enableSignUp && !signUpFormUIState.signupState.isLoggedIn && (
        <div>
          {!hideCaptchaEula && !signUpFormUIState.isConfluence && (
            <>
              {enableDefaultExpandedEmail ? (
                <ReCaptchaEulaDefaultExpandedEmail
                  onClick={() => {
                    segment(
                      'bxp.formCloudSignup.reCaptchaPrivacyPolicyClicked',
                      {}
                    );
                  }}
                >
                  <SanitizedFormattedHtmlMessage {...messages.reCaptchaLegal} />
                </ReCaptchaEulaDefaultExpandedEmail>
              ) : (
                <ReCaptchaEula
                  onClick={() => {
                    segment(
                      'bxp.formCloudSignup.reCaptchaPrivacyPolicyClicked',
                      {}
                    );
                  }}
                >
                  <SanitizedFormattedHtmlMessage {...messages.reCaptchaLegal} />
                </ReCaptchaEula>
              )}
            </>
          )}
        </div>
      )}
    </WacSignupFormContainer>
  );
}

WacSignupForm.messages = messages;

export default injectIntl(WacSignupForm);
